import React, { useState, ReactElement, useEffect, CSSProperties, ChangeEvent, useContext } from "react";
import {
  Button, Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle, FormControlLabel, IconButton, Switch,
  TextField, Tooltip, ToggleButton, ToggleButtonGroup
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { Add, Remove } from "@mui/icons-material";
import { cloneObject, getUID } from "../../../system/utils";
import { IDashboard } from "../types";
import { getEmptyCellConfig } from "./utils";
import { getPalette } from "../../../configuration/configuration";
import { SystemContext } from "../../../context/systemContext";

const maximumItems = 3;

interface IProps {
  dashboard: IDashboard;
  setDashboard: (dashboard: IDashboard) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

interface IDesign {
  title: string;
  isPrivate: boolean;
  isDefault: boolean;
  isAcquisition: boolean;
  isEditable: boolean;
  bot: boolean;
  range: number;
  rows: number[];
  marqueeTime: number;
  colorIndex: number;
}

interface IRowProps {
  parentKey: string;
  columns: number;
  setColumns: (columns: number) => void;
}

const buttonStyle = {
  alignSelf: "center",
  justifySelf: "center",
  width: 25,
  height: 25
};

const getTemplate = (cells: number) => {
  let template = "30px ";
  for (let i = 0; i < cells; i++) {
    template += "1fr ";
  }

  template += "30px ";

  return template;
};

const generateDesignData = (dashboard: IDashboard): IDesign => {
  const result: IDesign = {
    title: dashboard.name,
    isPrivate: dashboard.isPrivate,
    isDefault: dashboard.isDefault,
    isAcquisition: dashboard.isAcquisition ?? false,
    isEditable: dashboard.isEditable ?? false,
    range: dashboard.customConfig.range,
    bot: dashboard.customConfig.bot,
    rows: [],
    marqueeTime:  dashboard.customConfig.marqueeTime ?? 0,
    colorIndex:  dashboard.customConfig.colorIndex ?? 0
  };

  for (const item of dashboard.layout.rows) {
    result.rows.push(item.length);
  }

  return result;
};

const RowDesignComponent = ({ columns, parentKey, setColumns }: IRowProps): ReactElement => {
  const layoutStyle: CSSProperties = {
    gridTemplateColumns: getTemplate(columns),
    gridTemplateRows: "1fr",
    gap: "0.5em",
    width: "100%",
    height: "100%",
    position: "relative"
  };

  return (
    <div className="display-grid" style={layoutStyle} >
      <IconButton color="secondary" style={buttonStyle} disabled={columns === 1} onClick={() => setColumns(columns - 1)}>
        <Tooltip title={"Remove Column"}>
          <Remove />
        </Tooltip>
      </IconButton>
      {[...Array<number>(columns)].map((value, index) => {
        return <Card key={`${parentKey}-${value}-${index}`} style={{ background: "#ccc" }} />;
      })
      }
      <IconButton color="secondary" style={buttonStyle} disabled={columns === maximumItems} onClick={() => setColumns(columns + 1)}>
        <Tooltip title={"Add Column"}>
          <Add />
        </Tooltip>
      </IconButton>
    </div>
  );
};

const MainDialog = ({ dashboard, setDashboard, open, setOpen }: IProps): ReactElement => {
  const [ design, setDesign ] = useState<IDesign | null>(null);
  const systemContext = useContext(SystemContext);
  const showMarqueeTime = design && !design.isAcquisition && (design.rows.length > 1 || design.rows.length === 1 && design?.rows[0] > 1);

  useEffect(() => {
    setDesign(generateDesignData(dashboard));
  }, [ dashboard ]);

  const onchangeFilter = (event: ChangeEvent<HTMLInputElement>) => {
    if (design) {
      setDesign({ ...design, title: event.target.value });
    }
  };

  const onAccept = () => {
    if (!design) {
      return;
    }

    const newDashboard = cloneObject(dashboard);

    while (design.rows.length < newDashboard.layout.rows.length) {
      newDashboard.layout.rows.pop();
    }

    while (design.rows.length > newDashboard.layout.rows.length) {
      newDashboard.layout.rows.push([]);
    }

    for (let i = 0; i < design.rows.length; i++) {
      while (design.rows[i] < newDashboard.layout.rows[i].length){
        newDashboard.layout.rows[i].pop();
      }

      if (design?.isAcquisition !== newDashboard.isAcquisition) {
        for (let j = 0; j < newDashboard.layout.rows[i].length; j++) {
          const newCell = getEmptyCellConfig(design?.isAcquisition);
          newCell.key = `RC${i}${j}${getUID()}`;
          newCell.title = `row ${i + 1} - col ${newDashboard.layout.rows[i].length + 1}`;

          newDashboard.layout.rows[i][j] = newCell;
        }
      }

      while (design.rows[i] > newDashboard.layout.rows[i].length) {
        const newCell = getEmptyCellConfig(design?.isAcquisition);
        newCell.key = `RC${i}${newDashboard.layout.rows[i].length}${getUID()}`;
        newCell.title = `row ${i + 1} - col ${newDashboard.layout.rows[i].length + 1}`;

        newDashboard.layout.rows[i].push(newCell);
      }
    }

    newDashboard.name = design.title;
    newDashboard.isPrivate = design.isPrivate;
    newDashboard.isDefault = design.isDefault;
    newDashboard.isAcquisition = design.isAcquisition;
    newDashboard.isEditable = design.isEditable;
    newDashboard.customConfig.range = design.range;
    newDashboard.customConfig.bot = design.bot;
    newDashboard.customConfig.marqueeTime = design.marqueeTime;
    newDashboard.customConfig.colorIndex = design.colorIndex;

    setDashboard(newDashboard);
    setOpen(false);
  };

  const onCancel = () => {
    setOpen(false);
    setDesign(generateDesignData(dashboard));
  };

  const layoutStyle = {
    gridTemplateColumns: "1fr",
    gridTemplateRows: getTemplate(design ? design.rows.length : 1),
    gridRowGap: "0.5em",
    width: 400,
    height: 400
  };

  const onAddRow = () => {
    if (design) {
      const newDesign = cloneObject(design);
      newDesign.rows.push(1);
      setDesign(newDesign);
    }
  };

  const onRemoveRow = () => {
    if (design) {
      const newDesign = cloneObject(design);
      newDesign.rows.pop();
      setDesign(newDesign);
    }
  };

  const onUpdateColumns = (rowIndex: number, columns: number) => {
    if (design) {
      const newDesign = cloneObject(design);
      newDesign.rows[rowIndex] = columns;
      setDesign(newDesign);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (design) {

      if(event.target.name === "isAcquisition") {

        const checked = event.target.checked;
        const target = event.target.name;

        systemContext.showMessage({
          title: "Acquisition Mode",
          color: "warning",
          message: "Current configuration will be lost, Do you want to change isAcquisition value?",
          callback: () => {
            setDesign({ ...design, [target]: checked, marqueeTime: 0 });
          }
        });

      } else {
        setDesign({ ...design, [event.target.name]: event.target.checked });
      }
    }
  };

  const handleRange = (_event: React.MouseEvent<HTMLElement>, value: number | null) => {
    if (design) {
      const newValue = cloneObject(design);
      newValue.range = value ?? 1;
      setDesign(newValue);
    }
  };

  const handleMarquee = (_event: React.MouseEvent<HTMLElement>, value: number | null) => {
    if (design) {
      const newValue = cloneObject(design);
      newValue.marqueeTime = value ?? 0;
      setDesign(newValue);
    }
  };

  const onSelectColorIndex = (value: number) => {
    const newValue = cloneObject(design) as any;
    newValue.colorIndex = value;
    setDesign(newValue);
  };

  return(
    <Dialog onClose={onCancel} aria-labelledby="customized-dialog-title" open={open} maxWidth="md">

      <DialogTitle id="customized-dialog-title">
        Layout Config
      </DialogTitle>

      <DialogContent dividers={true} >
        <div className="display-flex-column p-2">
          <TextField
            variant="standard"
            placeholder={"Title"}
            label={"Title"}
            value={design?.title}
            onChange={onchangeFilter}/>

          <div className="display-flex-row">
            <FormControlLabel
              control={<Switch checked={design?.isPrivate} onChange={handleChange} name="isPrivate" />}
              label="Private"
            />
            <div className="flex-grow-1"/>
            <FormControlLabel
              labelPlacement="start"
              disabled={design?.isPrivate}
              control={<Switch checked={design?.isEditable} onChange={handleChange} name="isEditable" />}
              label="Allow Edit"
            />
          </div>

          <FormControlLabel
            control={<Switch checked={design?.isDefault} onChange={handleChange} name="isDefault" />}
            label="Default"
          />


          <div className="display-flex-row">
            <FormControlLabel
              control={<Switch checked={design?.isAcquisition} onChange={handleChange} name="isAcquisition" />}
              label="Acquisition"
            />
            <div className="flex-grow-1"/>
            <FormControlLabel labelPlacement="start"
              disabled={!design?.isAcquisition}
              control={<Switch checked={design?.bot} onChange={handleChange} name="bot" />}
              label="Bot"
            />
          </div>

          { design?.isAcquisition &&
            <FormControlLabel
              control={
                <ToggleButtonGroup
                  className="mr-2 ml-2"
                  value={design?.range ?? 1}
                  exclusive={true}
                  onChange={handleRange}>
                  <ToggleButton value={.0833} >5 min</ToggleButton>
                  <ToggleButton value={.25} >15 min</ToggleButton>
                  <ToggleButton value={.5} >30 min</ToggleButton>
                  <ToggleButton value={1} >1 hr</ToggleButton>
                  <ToggleButton value={2} >2 hrs</ToggleButton>
                  <ToggleButton value={4} >4 hrs</ToggleButton>
                  <ToggleButton value={8} >8 hrs</ToggleButton>
                  <ToggleButton value={12} >12 hrs</ToggleButton>
                </ToggleButtonGroup>
              }
              label="Range"
            />
          }

          { showMarqueeTime &&
            <FormControlLabel
              control={
                <ToggleButtonGroup
                  className="mr-2 ml-2"
                  value={design?.marqueeTime ?? 0}
                  exclusive={true}
                  onChange={handleMarquee}>
                  <ToggleButton value={0} >Disabled</ToggleButton>
                  <ToggleButton value={5} >5 s</ToggleButton>
                  <ToggleButton value={10} >10 s</ToggleButton>
                  <ToggleButton value={15} >15 s</ToggleButton>
                  <ToggleButton value={30} >30 s</ToggleButton>
                </ToggleButtonGroup>
              }
              label="Marquee Time"
            />
          }

          <FormControlLabel
            control={
              <div className="m-2 display-flex-row gap-1">
                {getPalette().rgbColor.map((c, index) => {

                  return (
                    <div key={`color-${index}`}
                      onClick={() => onSelectColorIndex(index)}
                      style={{ background: `rgb(${c})`,
                        width: 24,
                        height: 24,
                        border: design?.colorIndex === index ? "1px solid whitesmoke" : undefined,
                        borderRadius: 4 }} />
                  );
                })}
              </div>
            }
            label="Color"
          />

          <label className="mt-3">Layout</label>

          <div className="display-grid m-1" style={layoutStyle} >
            <IconButton color="secondary" style={buttonStyle} disabled={design?.rows.length === 1} onClick={onRemoveRow}>
              <Tooltip title={"Remove Row"}>
                <Remove />
              </Tooltip>
            </IconButton>
            { design?.rows.map((columns, index) => {
              const key = `design-row-${index}`;

              return <RowDesignComponent key={key} columns={columns} parentKey={key} setColumns={(cols) => onUpdateColumns(index, cols)} />;
            })
            }
            <IconButton color="secondary" style={buttonStyle} disabled={design?.rows.length === maximumItems} onClick={onAddRow}>
              <Tooltip title={"Add Row"}>
                <Add />
              </Tooltip>
            </IconButton>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onAccept} variant="contained" color="primary" startIcon={<CheckIcon />}>
          Accept
        </Button>
        <Button onClick={onCancel} variant="contained" color="secondary" startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MainDialog;
